import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MapContainer, TileLayer, Polygon, FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import { ArrowLeftIcon, MapPinIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { farmApi } from '../lib/api';
import toast from 'react-hot-toast';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

const cropTypes = ['Corn', 'Wheat', 'Soybeans', 'Barley', 'Oats', 'Canola', 'Rice', 'Cotton', 'Vegetables', 'Other'];

export default function FarmCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [cropType, setCropType] = useState('');
  const [plantingDate, setPlantingDate] = useState('');
  const [boundary, setBoundary] = useState<[number, number][]>([]);

  useEffect(() => {
    const state = location.state as { coordinates?: [number, number][] };
    if (state?.coordinates) setBoundary(state.coordinates);
  }, [location.state]);

  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof farmApi.create>[0]) => farmApi.create(data),
    onSuccess: (farm) => {
      queryClient.invalidateQueries({ queryKey: ['farms'] });
      toast.success('Farm created!');
      navigate(`/farms/${farm.id}`);
    },
    onError: () => toast.error('Failed to create farm'),
  });

  const handleDrawCreated = (e: any) => {
    const layer = e.layer;
    const latLngs = layer.getLatLngs()[0];
    const coords: [number, number][] = latLngs.map((ll: any) => [ll.lat, ll.lng]);
    setBoundary(coords);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Enter a farm name');
    if (boundary.length < 3) return toast.error('Draw a boundary on the map');

    createMutation.mutate({
      name: name.trim(),
      boundary: boundary.map(([lat, lon]) => ({ lat, lon })),
      cropType: cropType || undefined,
      plantingDate: plantingDate || undefined,
    });
  };

  const estimatedArea = boundary.length >= 3
    ? Math.abs(boundary.reduce((area, [lat, lon], i) => {
        const [lat2, lon2] = boundary[(i + 1) % boundary.length];
        return area + lon * lat2 - lon2 * lat;
      }, 0) / 2) * 111000 * 111000 / 10000
    : 0;

  return (
    <div className="p-6">
      <div className="flex items-center space-x-4 mb-6">
        <Link to="/farms" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add New Farm</h1>
          <p className="text-gray-500">Draw boundaries and enter details</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-semibold flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2 text-green-600" />
              Draw Farm Boundary
            </h2>
            <p className="text-sm text-gray-500 mt-1">Use polygon tool to draw boundaries</p>
          </div>
          <div className="h-[500px]">
            <MapContainer center={[44.38, -79.7]} zoom={12} className="h-full w-full">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Esri"
              />
              <FeatureGroup>
                <EditControl
                  position="topright"
                  onCreated={handleDrawCreated}
                  draw={{
                    rectangle: { showArea: false, shapeOptions: { color: '#10b981', weight: 3, fillOpacity: 0.3 } },
                    circle: false,
                    circlemarker: false,
                    marker: false,
                    polyline: false,
                    polygon: { showArea: false, shapeOptions: { color: '#10b981', weight: 3, fillOpacity: 0.3 } },
                  }}
                />
              </FeatureGroup>
              {boundary.length > 0 && (
                <Polygon positions={boundary} pathOptions={{ color: '#10b981', fillOpacity: 0.3 }} />
              )}
            </MapContainer>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="font-semibold mb-4">Farm Details</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Farm Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., North Field"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Crop Type</label>
              <select
                value={cropType}
                onChange={(e) => setCropType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">Select crop</option>
                {cropTypes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Planting Date</label>
              <input
                type="date"
                value={plantingDate}
                onChange={(e) => setPlantingDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Boundary Status</h3>
              {boundary.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    <span className="text-sm">Boundary drawn</span>
                  </div>
                  <p className="text-sm text-gray-600">Points: {boundary.length}</p>
                  <p className="text-sm text-gray-600">Est. Area: {estimatedArea.toFixed(2)} ha</p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Draw boundary on map</p>
              )}
            </div>

            <button
              type="submit"
              disabled={createMutation.isPending || boundary.length < 3}
              className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Farm'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
